import { ConfidenceChip } from "./ConfidenceChip";
import { ChevronsUpDown, RotateCcw, History, Undo2 } from "lucide-react";

const PRIMARY_KEY = "provider_vs_deployer";

const AssumptionRow = ({ a, value, onChange, onRevertKey, prominent, overridden }) => {
  const options = a.options && a.options.length ? a.options : ["yes", "no"];
  const needsConfirm = a.needs_confirmation || a.confidence === "low";
  return (
    <div
      data-testid={`assumption-${a.key}`}
      className={`rounded-lg p-4 transition-colors ${
        prominent
          ? "border border-asphalt bg-asphalt"
          : `border bg-white hover:border-asphalt/40 ${needsConfirm ? "border-tier-prohibited/50" : "border-line"}`
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex-1">
          {prominent && (
            <span className="mb-1 inline-block text-[11px] font-bold uppercase tracking-wider text-aqua">
              Most important
            </span>
          )}
          <p className={`text-sm font-medium ${prominent ? "text-white" : "text-asphalt"}`}>{a.question}</p>
        </div>
        <ConfidenceChip level={a.confidence} needsConfirm={needsConfirm} />
      </div>

      <div className="relative mt-3">
        <select
          data-testid={`assumption-select-${a.key}`}
          value={value}
          onChange={(e) => onChange(a.key, e.target.value)}
          className={`w-full appearance-none rounded-brand border px-3 py-2.5 pr-9 text-sm font-medium outline-none ${
            prominent
              ? "border-white/25 bg-white/10 text-white focus:border-aqua"
              : "border-line bg-white text-asphalt focus:border-asphalt"
          }`}
        >
          {options.map((o) => (
            <option key={o} value={o} className="text-asphalt">
              {o}
            </option>
          ))}
        </select>
        <ChevronsUpDown className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 ${prominent ? "text-white/70" : "text-muted"}`} />
      </div>

      <div className="mt-2 flex items-start justify-between gap-2">
        {a.rationale ? (
          <p className={`text-xs leading-relaxed ${prominent ? "text-white/80" : "text-muted"}`}>
            {a.rationale}
            {a.source ? (
              <span className={`ml-1 ${prominent ? "text-white/70" : "text-muted/80"}`}>
                (
                {a.source.startsWith("http") ? (
                  <a href={a.source} target="_blank" rel="noreferrer" className={`underline ${prominent ? "hover:text-aqua" : "hover:text-asphalt"}`}>
                    source
                  </a>
                ) : (
                  a.source
                )}
                )
              </span>
            ) : null}
          </p>
        ) : (
          <span />
        )}
        {overridden && (
          <button
            onClick={() => onRevertKey(a.key)}
            data-testid={`assumption-revert-${a.key}`}
            className={`flex shrink-0 items-center gap-1 text-[11px] font-medium ${prominent ? "text-aqua hover:text-white" : "text-asphalt hover:text-aqua-dark"}`}
            title="Revert to the AI's original draft answer"
          >
            <Undo2 className="h-3 w-3" /> Revert
          </button>
        )}
      </div>
    </div>
  );
};

const ChangeTrail = ({ changeLog }) => {
  if (!changeLog || changeLog.length === 0) return null;
  return (
    <div className="rounded-lg border border-line bg-tint p-4" data-testid="change-trail">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-asphalt" />
        <span className="text-sm font-bold text-asphalt">What changed</span>
        <span className="text-xs text-muted">({changeLog.length})</span>
      </div>
      <ul className="mt-3 space-y-2">
        {changeLog.slice(0, 8).map((c, i) => (
          <li key={i} className="flex flex-wrap items-center gap-1.5 text-xs text-muted" data-testid="change-entry">
            <span className="font-medium text-asphalt">{c.question}</span>
            <span className="rounded bg-white px-1.5 py-0.5 text-muted line-through">{String(c.from) || "unset"}</span>
            <span className="text-muted">to</span>
            <span
              className="rounded px-1.5 py-0.5 font-medium text-asphalt"
              style={{ background: "rgba(47,225,236,0.20)" }}
            >
              {String(c.to)}
            </span>
            <span className="ml-auto text-[10px] uppercase tracking-wide text-muted/70">{c.via}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AssumptionsPanel = ({ assumptions, answers, onChange, changeLog, onRevertKey, onResetOriginal }) => {
  if (!assumptions || assumptions.length === 0) return null;
  const current = (a) => (answers[a.key] !== undefined ? answers[a.key] : a.assumed_value);
  const primary = assumptions.find((a) => a.key === PRIMARY_KEY);
  const rest = assumptions.filter((a) => a.key !== PRIMARY_KEY);
  const hasEdits = Object.keys(answers || {}).length > 0;

  return (
    <section
      data-testid="assumptions-panel"
      className="space-y-4 rounded-xl border border-line bg-white p-6 shadow-sm md:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-asphalt md:text-xl">Assumptions we made</h2>
          <p className="mt-1 text-sm text-muted">
            These pre-filled inferences drive the verdict. Change any control and the classification re-runs
            live. Low-confidence answers are flagged for you to confirm.
          </p>
        </div>
        {hasEdits && (
          <button
            onClick={onResetOriginal}
            data-testid="reset-original-btn"
            className="gl-secondary-btn flex shrink-0 items-center gap-2 px-3 py-2 text-xs"
            title="Reset every assumption to the AI's original first-pass draft"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Revert to original draft
          </button>
        )}
      </div>

      <ChangeTrail changeLog={changeLog} />

      {primary && (
        <AssumptionRow
          a={primary}
          value={current(primary)}
          onChange={onChange}
          onRevertKey={onRevertKey}
          overridden={answers[primary.key] !== undefined}
          prominent
        />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {rest.map((a) => (
          <AssumptionRow
            key={a.key}
            a={a}
            value={current(a)}
            onChange={onChange}
            onRevertKey={onRevertKey}
            overridden={answers[a.key] !== undefined}
          />
        ))}
      </div>
    </section>
  );
};
